import type { ButtonHTMLAttributes, Ref } from 'react'
import { cx } from '../cx'
import { Icon, type IconName } from '../icons'
import styles from './IconButton.module.css'
import { Tooltip, type TooltipPlacement } from './Tooltip'

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'aria-label'
> {
  icon: IconName
  /** Required: becomes the accessible name and the visible tooltip. */
  label: string
  size?: 'md' | 'lg'
  tooltipPlacement?: TooltipPlacement
  ref?: Ref<HTMLButtonElement>
}

/**
 * Icon-only button for universal controls (close, previous/next). Everywhere else use
 * Button with an icon and a visible label (docs/research/05 §5.2).
 */
export function IconButton({
  icon,
  label,
  size = 'md',
  tooltipPlacement = 'top',
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <Tooltip content={label} placement={tooltipPlacement} role="label">
      <button
        type={type}
        aria-label={label}
        className={cx(styles.iconButton, styles[size], className)}
        {...rest}
      >
        <Icon name={icon} size={size === 'lg' ? 24 : 22} />
      </button>
    </Tooltip>
  )
}
